import React, { useState, useEffect } from 'react';
import * as consts from 'config/consts';
import Icon from 'components/commons/icon/Icon';
import PositionItem from './PositionItem';

import { GetPositionsResultItem, GetUserBrokerResultItem } from 'config/api';
import { expandTypes } from 'config/consts';
import { useCurrencyMode } from 'hooks/useCurrencyMode';
import { AboutExchangeRateModalButton } from '../home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import { ToggleButton } from '../home/PortfolioRatioStuffing/PortfolioRatioStuffingList';
import { isForeignStockTypes } from '../utils/productTypes';

// レコード数にあわせて、アニメーション可動域を算出
const calcMaxHeight = (expandType: number, positionsLength: number): string => {
  const zero = '0px';
  if (expandType === expandTypes.close) return zero;

  const itemHeight = document.getElementById('id_positionItem')?.offsetHeight;
  if (itemHeight === undefined) return zero;

  const selectorHeight =
    document.getElementById('id_positionList_sort')?.offsetHeight ?? 0;
  return `${selectorHeight + itemHeight * positionsLength}px`;
};

type PositionListProps = {
  positions: Array<GetPositionsResultItem>;
  brokerItem: GetUserBrokerResultItem;
  sortable: boolean;
  expandable: boolean;
  setUserPositions?: (positions: Array<GetPositionsResultItem>) => void;
};

const PositionList: React.FC<PositionListProps> = props => {
  const {
    positions,
    brokerItem,
    sortable,
    expandable,
    setUserPositions,
  } = props;

  const [expandType, setExpandType] = useState<number>(expandTypes.close);
  const expandIcon =
    expandType === expandTypes.open
      ? { name: 'chevron-down transform rotate-180', label: '閉じる' }
      : { name: 'chevron-down transform rotate-0', label: '内訳を表示' };
  const currencyMode = useCurrencyMode();

  const [sortType, setSortType] = useState('0');

  const [positionElements, setPositionElements] = useState<
    Array<JSX.Element>
  >();

  useEffect(() => {
    const cashPositions = positions.filter(
      position => position.ProductType === consts.productTypes.預金_MRF
    );
    const sortNotCashPositions = positions
      .filter(position => position.ProductType !== consts.productTypes.預金_MRF)
      .sort((first, second) => {
        // '0':降順 '1':昇順
        const isDesc = sortType === '0';
        const firstProfitOrLoss = first.ProfitOrLoss ?? 0;
        const secondProfitOrLoss = second.ProfitOrLoss ?? 0;
        if (firstProfitOrLoss > secondProfitOrLoss) return isDesc ? -1 : 1;
        if (firstProfitOrLoss < secondProfitOrLoss) return isDesc ? 1 : -1;
        return 0;
      });

    const sortedPositions = sortNotCashPositions.concat(cashPositions);

    setPositionElements(
      sortedPositions.map((value, index) => {
        return (
          <PositionItem
            data={value}
            brokerItem={brokerItem}
            key={index}
            currencyMode={currencyMode.currencyMode}
            setUserPositions={setUserPositions}
          />
        );
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    positions,
    brokerItem,
    setPositionElements,
    sortType,
    currencyMode.currencyMode,
  ]);

  const onClickExpand = (): void => {
    setExpandType(
      expandType === expandTypes.open ? expandTypes.close : expandTypes.open
    );
  };

  // max-h-custom1・max-h-0の指定を止めて、maxHeightを算出
  const maxHeight = calcMaxHeight(expandType, positions.length);
  const openCloseAnimation = expandable
    ? expandType === expandTypes.open
      ? 'ui-effect-fade-in-300 visible'
      : 'ui-effect-fade-out-300 invisible'
    : null;

  const options = ['評価損益 高い順', '評価損益 低い順'].map((value, index) => {
    return (
      <option value={index} key={index}>
        {value}
      </option>
    );
  });
  const onSortChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSortType(e.target.value);
  };
  const isShowToggleButton = positions.some(item =>
    isForeignStockTypes(item.ProductType)
  );

  if (positions.length === 0) return null;

  return (
    <div className="mx-3">
      <div
        className={`transition-all duration-300 ${openCloseAnimation}`}
        style={{ maxHeight: expandable ? `${maxHeight}` : '' }}>
        {sortable && (
          <div
            className="flex items-center ui-border-t ui-border-b"
            id="id_positionList_sort">
            <div className="flex-grow py-3 px-4 flex items-center">
              <span className="mr-1">内訳</span>
              {isShowToggleButton && <ToggleButton {...currencyMode} />}
            </div>
            <div className="relative">
              <select
                className="appearance-none h-8 w-48 px-4 bg-gray-200 border border-gray-300 rounded cursor-pointer"
                value={sortType}
                onChange={(e): void => onSortChange(e)}>
                {options}
              </select>
              <div className="absolute pointer-events-none inset-y-0 right-0 flex items-center pr-1">
                <i className="icon-arrow_drop_down text-xl text-gray-700" />
              </div>
            </div>
          </div>
        )}

        {positionElements}
      </div>
      {expandable ? (
        <div className="cursor-pointer" onClick={onClickExpand}>
          <div className="flex justify-center items-center text-gray-500 my-2">
            <Icon
              className="text-2xl transition duration-300"
              name={expandIcon.name}
            />
            <div className="ml-1 text-sm">{expandIcon.label}</div>
          </div>
        </div>
      ) : null}
      {brokerItem.BrokerType === consts.BrokerType.確定拠出年金 && (
        <div className="mt-2 mb-4 text-xs text-bluegray-500">
          ※この口座で「定期預金」「保険商品」を保有している場合は「預金」として扱われます。なお、両方お持ちの場合は合算した金額で表示されます。
        </div>
      )}
      <div className="flex mr-3 mt-4">
        <AboutExchangeRateModalButton
          isShowMessage
          className="ml-auto text-gray-500"
        />
      </div>
    </div>
  );
};

export default PositionList;
